import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import Icon, { IconType } from 'components/Icon';
import IconButton, { IconButtonState } from 'components/Widget/Button/IconButton';
import Pill from 'components/Widget/Pill';
import SubmitButton, { SubmitButtonState } from 'components/Widget/Button/SubmitButton';
import AttachmentStrip from 'components/Widget/AttachmentStrip';
import MultilineInput from 'components/Widget/Input/MultilineInput';
import ThumbnailStrip from 'components/Widget/ThumbnailStrip';
import { dsTabToPick, resolveDsPicksToDisplayNames } from 'designSystem/content/util';
import * as api from 'dragonmouse-shared/api/forge';
import { ChatMode, ChatStatus, DsChat, DsPick } from 'dragonmouse-shared/interfaces';
import { NEW_CHAT_TITLE } from 'dragonmouse-shared/constants';
import { designSystemEditingBlocked } from 'redux/db/util';
import { setActiveDsChatId, setHistoricalDsVersionNumber } from 'redux/mode/actions';
import { displayDsReadOnlyModal } from 'redux/mode/util/modals';
import { interceptIfNoCredits } from 'economy';
import { v4 as uuid } from 'uuid';
import { useDsChatQueue } from 'hooks/useDsChatQueue';
import { useFileUploadCallbacks } from 'hooks/useFileUploadCallbacks';
import useDsNavigation from 'hooks/useDsNavigation';
import useToast from 'hooks/useToast';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { selectActiveDesignSystem, selectFrame } from 'redux/db/reducers';
import { clearDsPicks, removeDsPick, setPendingDsChatInput } from 'redux/mode/actions';
import { selectActiveDsTab, selectIsMobile } from 'redux/mode/reducers';
import { RootReducer } from 'redux/reducers';
import store from 'redux/store';
import { useTheme } from 'styled-components';
import * as routes from 'routes';
import { cancelUpload, onUploadFileDialog } from 'utils';
import { useChatInputHandlers } from '../../hooks/useChatInputHandlers';
import { useChatInputValidation } from '../../hooks/useChatInputValidation';
import * as Base from '../styles';
import BreakageNotification from './BreakageNotification';

interface DsChatInputProps {
	dsChat: DsChat;
	isLegacy?: boolean;
}

const DsChatInput = ({ dsChat, isLegacy }: DsChatInputProps) => {
	const dispatch = useDispatch();
	const theme = useTheme();
	const { openPickTab } = useDsNavigation();
	const { message: toastMessage, isVisible: isToastVisible, showToast } = useToast();

	const {
		activeDesignSystem,
		pendingDsChatInput,
		isMobile,
		selectedDsPicks,
		designSystemContent,
		activeTab,
		historicalDsVersionNumber,
	} = useSelector((r: RootReducer) => {
		return {
			activeDesignSystem: selectActiveDesignSystem(r.db, r.mode.activeDesignSystemId),
			pendingDsChatInput: r.mode.pendingDsChatInputs[dsChat.id],
			isMobile: selectIsMobile(r.mode),
			selectedDsPicks: r.mode.selectedDsPicks,
			designSystemContent: r.mode.designSystemContent,
			activeTab: selectActiveDsTab(r.mode),
			historicalDsVersionNumber: r.mode.historicalDsVersionNumber,
		};
	}, shallowEqual);

	const isViewingHistorical = historicalDsVersionNumber !== null;
	const effectiveChatMode = isViewingHistorical
		? ChatMode.CHAT_ONLY
		: pendingDsChatInput?.chatMode || ChatMode.DESIGN_AND_CHAT;
	const [prompt, setPrompt] = useState<string>(pendingDsChatInput?.message || '');
	const [isFocused, setIsFocused] = useState<boolean>(false);
	const [isCreatingChat, setIsCreatingChat] = useState(false);
	const inputRef = useRef<HTMLTextAreaElement>(null);

	useEffect(() => {
		if (!pendingDsChatInput) {
			dispatch(
				setPendingDsChatInput({
					dsChatId: dsChat.id,
					pendingDsChatInput: {
						message: '',
						chatMode: ChatMode.DESIGN_AND_CHAT,
					},
				})
			);
		}
	}, [dsChat.id, pendingDsChatInput, dispatch]);

	const stoppable = dsChat?.metadata?.status === ChatStatus.STOPPABLE;

	const queue = useDsChatQueue(dsChat?.id ?? null);

	// Validation logic for whether input can be submitted
	const { canSubmit } = useChatInputValidation({
		prompt,
		loadingAttachments: pendingDsChatInput?.loadingAttachments,
	});

	const resolvedDsPicks = useMemo(
		() => resolveDsPicksToDisplayNames(selectedDsPicks, designSystemContent ?? null),
		[selectedDsPicks, designSystemContent]
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------
	const onSubmit = () => {
		if (!activeDesignSystem || !dsChat) return;
		if (!canSubmit) return;

		const blocked = designSystemEditingBlocked();
		if (blocked && blocked !== 'historical') {
			displayDsReadOnlyModal(blocked);
			return;
		}

		// Check credits/wallet - interceptIfNoCredits shows appropriate modal per user type
		if (interceptIfNoCredits({ kind: 'chatEdit', designSystemId: dsChat.designSystemId })) {
			return;
		}

		const entry: api.AddUserEntryDsChatRequest['entry'] = {
			kind: 'user',
			message: prompt,
			attachments: pendingDsChatInput?.attachments || [],
			sourceFrameIds: pendingDsChatInput?.frameIds || [],
			...(selectedDsPicks.length > 0 && { selectedDsPicks: [...selectedDsPicks] }),
			...(activeTab && { viewingDsPick: dsTabToPick(activeTab) }),
			...(historicalDsVersionNumber !== null && {
				viewingDsVersionNumber: historicalDsVersionNumber,
			}),
		};

		// Submission goes through the chat action queue. Idle chat → fires
		// immediately; busy chat → waits in FIFO and fires when idle. User
		// can rapid-fire and the queue serializes.
		const trimmedPrompt = prompt.trim();
		const firstAttachmentName = entry.attachments?.[0]?.filename;
		const label = (trimmedPrompt || firstAttachmentName || 'Message').slice(0, 80);
		queue.enqueue({
			label,
			payload: { kind: 'addUserEntry', entry, chatMode: effectiveChatMode },
		});

		setPrompt('');
		dispatch(clearDsPicks());

		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					message: '',
					attachments: [],
					frameIds: [],
				},
			})
		);
	};

	const onStop = async () => {
		if (!activeDesignSystem || !dsChat) return;

		await forge.updateDsChat({
			update: {
				id: dsChat.id,
				metadata: {
					status: ChatStatus.PROCESSING,
					statusMessage: 'Stopping...',
				},
			},
		});
		const req: api.StopDsChatGenerationRequest = {
			dsChatId: dsChat.id,
		};
		await forge.stopDsChatGeneration.withToast()(req);
	};

	// Callback for updating prompt - handles both local state and Redux
	const onUpdatePrompt = useCallback(
		(prompt: string) => {
			setPrompt(prompt);
			dispatch(
				setPendingDsChatInput({
					dsChatId: dsChat.id,
					pendingDsChatInput: {
						...pendingDsChatInput,
						message: prompt,
					},
				})
			);
		},
		[setPrompt, dispatch, dsChat.id, pendingDsChatInput]
	);

	// Parses frame IDs from pasted URLs. Frame URLs look like:
	// https://studio.moonchild.ai/project/:sceneId/:title?frameId=xxx&edit=false
	// returns true if the text contained frame URLs (suppresses default paste)
	const parseAndAttachFrameUrls = useCallback(
		(text: string): boolean => {
			const frameIds: string[] = [];
			for (const line of text.split('\n')) {
				try {
					const url = new URL(line.trim());
					if (url.origin !== window.location.origin) continue;
					const frameId = url.searchParams.get('frameId');
					if (frameId) frameIds.push(frameId);
				} catch {
					// not a URL
				}
			}
			if (frameIds.length > 0) {
				const existing = pendingDsChatInput?.frameIds || [];
				const merged = [...existing, ...frameIds.filter((id) => !existing.includes(id))];
				dispatch(
					setPendingDsChatInput({
						dsChatId: dsChat.id,
						pendingDsChatInput: { ...pendingDsChatInput, frameIds: merged },
					})
				);
				return true;
			}
			return false;
		},
		[dispatch, dsChat.id, pendingDsChatInput]
	);

	// File upload callbacks using Redux dispatch
	const fileCallbacks = useFileUploadCallbacks({
		type: 'ds',
		chatId: dsChat.id,
	});

	// Shared input handlers
	const { onKeyDown, onPaste, onContextMenu } = useChatInputHandlers({
		prompt,
		inputRef,
		canSubmit,
		onSubmit,
		onUpdatePrompt,
		fileCallbacks,
		chatEntries: dsChat.entries,
		pendingAttachments: pendingDsChatInput?.attachments,
		onBeforePaste: parseAndAttachFrameUrls,
	});

	const onRemoveThumbnail = (frameId: string) => {
		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					frameIds: (pendingDsChatInput?.frameIds || []).filter((id) => id !== frameId),
				},
			})
		);
	};

	const onThumbnailClick = (frameId: string) => {
		const frame = selectFrame(store.getState().db, frameId);
		const sceneId = frame?.sceneId;
		if (sceneId) {
			const scene = store.getState().db.scenes.find((s) => s.id === sceneId);
			const url = routes.frame(sceneId, scene?.title || 'Untitled', frameId);
			window.open(url, '_blank', 'noopener,noreferrer');
		}
	};

	const onRemoveAttachment = (storagePath: string) => {
		dispatch(
			setPendingDsChatInput({
				dsChatId: dsChat.id,
				pendingDsChatInput: {
					...pendingDsChatInput,
					attachments: pendingDsChatInput.attachments?.filter((a) => a.storagePath !== storagePath),
				},
			})
		);
	};

	const onPickClick = (pick: DsPick) => {
		openPickTab(pick);
	};

	const onNewChat = async () => {
		if (!activeDesignSystem || isCreatingChat) return;
		const blocked = designSystemEditingBlocked();
		if (blocked && blocked !== 'historical') {
			displayDsReadOnlyModal(blocked);
			return;
		}

		const chatId = uuid();
		const oldActiveDsChatId = dsChat.id;
		setIsCreatingChat(true);

		try {
			await forge.createDsChat(
				{
					id: chatId,
					designSystemId: activeDesignSystem.id,
					title: NEW_CHAT_TITLE,
					metadata: { status: ChatStatus.PROCESSING, statusMessage: 'Initializing chat...' },
				},
				true
			);
			dispatch(setActiveDsChatId(chatId));
		} catch (e) {
			dispatch(setActiveDsChatId(oldActiveDsChatId));
			throw e;
		} finally {
			setIsCreatingChat(false);
		}
	};

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	const renderDsPicks = () => {
		if (resolvedDsPicks.length === 0) return null;
		return (
			<Base.DsPicks>
				{resolvedDsPicks.map(({ pick, name }) => {
					const key =
						pick.kind === 'token'
							? `token-${pick.cssVariable}`
							: pick.kind === 'recipe'
							? `recipe-${pick.className}`
							: pick.kind === 'component'
							? `comp-${pick.tagName}`
							: pick.kind === 'guideline'
							? `guideline-${pick.guidelinePath}`
							: pick.kind === 'gallery'
							? `gallery-${pick.galleryPath}`
							: pick.kind === 'section'
							? `section-${pick.section}`
							: `asset-${pick.assetPath}`;
					return (
						<Pill
							key={key}
							label={name}
							tooltip="Click to open in design system"
							onClick={() => onPickClick(pick)}
							onRemove={() => dispatch(removeDsPick(pick))}
						/>
					);
				})}
				{resolvedDsPicks.length > 3 && (
					<Base.ClearAllButton onClick={() => dispatch(clearDsPicks())}>
						Clear all
					</Base.ClearAllButton>
				)}
			</Base.DsPicks>
		);
	};

	const renderAttachments = () => {
		const allAttachments = pendingDsChatInput?.attachments || [];
		const loading = pendingDsChatInput?.loadingAttachments;
		if (!allAttachments.length && !loading?.length) return null;
		return (
			<AttachmentStrip
				attachments={allAttachments}
				loadingAttachments={loading}
				onRemove={onRemoveAttachment}
				onCancel={cancelUpload}
			/>
		);
	};

	const renderAttachmentButton = () => {
		return (
			<IconButton
				icon={IconType.ADD}
				label={undefined}
				tooltip="Upload attachment"
				onClick={() => {
					onUploadFileDialog(fileCallbacks, true);
				}}
			/>
		);
	};

	const renderChatModeToggle = () => {
		const isChatOnly = effectiveChatMode === ChatMode.CHAT_ONLY;
		return (
			<IconButton
				state={isChatOnly ? IconButtonState.SELECTED : IconButtonState.REGULAR}
				icon={isChatOnly ? IconType.CHAT : IconType.STYLE}
				tooltip={isChatOnly ? 'Turn off Chat-only Mode' : 'Turn on Chat-only Mode'}
				colors={{
					selected: theme.green[200],
					hover: isChatOnly ? theme.green[400] : theme.gray[200],
					active: 'transparent',
					text: isChatOnly ? theme.green[800] : theme.gray[1000],
				}}
				onClick={() => {
					const nextChatMode =
						pendingDsChatInput?.chatMode === ChatMode.DESIGN_AND_CHAT
							? ChatMode.CHAT_ONLY
							: ChatMode.DESIGN_AND_CHAT;
					trackDesignSystemAction('toggle_chat_mode', { mode: nextChatMode });
					dispatch(
						setPendingDsChatInput({
							dsChatId: dsChat.id,
							pendingDsChatInput: {
								...pendingDsChatInput,
								chatMode: nextChatMode,
							},
						})
					);

					if (isMobile) {
						showToast(
							nextChatMode === ChatMode.CHAT_ONLY ? 'Chat-only mode' : 'Chat + design mode'
						);
					}
				}}
			/>
		);
	};

	const renderActions = () => {
		// Submit is always available — submissions go through the chat action
		// queue, so they enqueue when the chat is busy. When the chat is
		// stoppable we swap the submit icon for stop (clicking stops the
		// current run; the queue is preserved). The processing-only spinner
		// affordance is gone — the QueueIndicator above the input shows
		// in-flight/queued state.
		return (
			<Base.Actions isMobile={isMobile}>
				{!isMobile && renderAttachmentButton()}
				{!isViewingHistorical && renderChatModeToggle()}
				<Base.Spacer />
				<Base.SubmitButtonWrapper isMobile={isMobile}>
					<SubmitButton
						state={
							stoppable || canSubmit ? SubmitButtonState.REGULAR : SubmitButtonState.DISABLED
						}
						colors={
							effectiveChatMode === ChatMode.CHAT_ONLY
								? {
										disabled: theme.green[200],
										hover: theme.green[600],
										active: theme.green[800],
								  }
								: undefined
						}
						type={stoppable ? IconType.STOP : IconType.LINE_ARROW_UP}
						tooltip={stoppable ? 'Stop' : 'Submit'}
						tooltipPlacement="bottom-start"
						onClick={() => (stoppable ? onStop() : onSubmit())}
					/>
				</Base.SubmitButtonWrapper>
			</Base.Actions>
		);
	};

	return (
		<>
			{isMobile && toastMessage && (
				<Base.MobileToast isVisible={isToastVisible} role="status" aria-live="polite">
					{toastMessage}
				</Base.MobileToast>
			)}
			<Base.Base isMobile={isMobile}>
				{isLegacy && (
					<Base.LegacyOverlay isMobile={isMobile}>
						<Base.LegacyMessage isMobile={isMobile}>
							This chat uses a legacy format and can't accept new messages.
						</Base.LegacyMessage>
						<Base.LegacyNewChatButton
							isMobile={isMobile}
							onClick={onNewChat}
							disabled={isCreatingChat}
						>
							<Icon
								type={IconType.ADD}
								width={isMobile ? 16 : 14}
								height={isMobile ? 16 : 14}
								color={theme.gray[0]}
							/>
							Start New Chat
						</Base.LegacyNewChatButton>
					</Base.LegacyOverlay>
				)}
				<BreakageNotification dsChat={dsChat} />
				<Base.Prompt isFocused={isFocused}>
					{isViewingHistorical && (
						<Base.HistoricalRibbon>
							Viewing v{historicalDsVersionNumber} · chat-only mode
							<Base.RibbonLink onClick={() => dispatch(setHistoricalDsVersionNumber(null))}>
								Switch to latest
							</Base.RibbonLink>
						</Base.HistoricalRibbon>
					)}
					<ThumbnailStrip
						frameIds={pendingDsChatInput?.frameIds || []}
						onRemove={onRemoveThumbnail}
						onClick={onThumbnailClick}
					/>
					{renderDsPicks()}
					{renderAttachments()}
					<Base.MultilineInputWrapper isMobile={isMobile} onPaste={onPaste}>
						<MultilineInput
							data-ds-chat-input="true"
							ref={inputRef}
							value={prompt}
							onChange={(e) => onUpdatePrompt(e.currentTarget.value)}
							onKeyDown={onKeyDown}
							placeholder={'Chat about your design system'}
							minLines={isMobile ? 1 : 4}
							renderBorder={false}
							variant={isMobile ? 'large' : 'medium'}
							onFocusChange={(isFocused) => {
								setIsFocused(isFocused);
							}}
							onContextMenu={onContextMenu}
						/>
					</Base.MultilineInputWrapper>
					{renderActions()}
				</Base.Prompt>
			</Base.Base>
		</>
	);
};

export default DsChatInput;
